import AccordionNavV3Button from '@/components/layout/link/accordion-nav-v3-button';
import { cn } from '@/lib/cn';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { getDateFnsLocale } from '@/utils/dates';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { format } from 'date-fns';
import { LoaderCircle } from 'lucide-react';
import { memo, type ComponentProps } from 'react';

type SessionItem = NonNullable<ComponentProps<typeof AccordionNavV3Button>['session']>;

export type SessionsListItemProps = {
  dateFormat: string;
  locale: string;
  timeFormatter: Intl.DateTimeFormat;
  handleSessionSelection: (sessionId: string, selected: boolean) => void;
  index: number;
  integrationSwitchPatient: ComponentProps<
    typeof AccordionNavV3Button
  >['integrationSwitchPatient'];
  isSelected: boolean;
  isFetchingNextPage: boolean;
  isMobile: boolean;
  onSessionDeleted: ComponentProps<typeof AccordionNavV3Button>['onSessionsDeleted'];
  session: SessionItem;
  sessionSearchResult: SessionItem[];
  sessions: SessionItem[];
  setIsMobileHeaderSheetOpen: (value: boolean) => void;
  setSelectedSessionIds: (value: string[]) => void;
  showCheckbox: boolean;
  shouldHighlightFirstSession: boolean;
};

export const SessionsListItem = memo(function SessionsListItem({
  dateFormat,
  locale,
  timeFormatter,
  handleSessionSelection,
  index,
  integrationSwitchPatient,
  isSelected,
  isFetchingNextPage,
  isMobile,
  onSessionDeleted,
  session,
  sessionSearchResult,
  sessions,
  setIsMobileHeaderSheetOpen,
  setSelectedSessionIds,
  showCheckbox,
  shouldHighlightFirstSession,
}: SessionsListItemProps) {
  const sessionTime = parseTimeStamp(
    session.created_time_local || session.created_at,
    !session.ehr_appt_id
  );
  const dateFnsLocale = getDateFnsLocale(locale);
  const sessionDateDisplay = format(sessionTime, dateFormat, { locale: dateFnsLocale });
  const prevSession = sessionSearchResult.at(index - 1);
  const prevDate = prevSession
    ? format(
        parseTimeStamp(
          prevSession.created_time_local || prevSession.created_at,
          !prevSession.ehr_appt_id
        ),
        dateFormat,
        { locale: dateFnsLocale }
      )
    : null;
  const currentDate = format(sessionTime, dateFormat, { locale: dateFnsLocale });
  const showDateHeader = index === 0 || currentDate !== prevDate;
  const onMobileSideBarDismiss = () => {
    if (isMobile) {
      setIsMobileHeaderSheetOpen(false);
    }
  };
  const isOfflineSyntheticSession = Boolean(session.is_offline_synthetic);
  const onSelected = (selected: boolean) => {
    if (isOfflineSyntheticSession) return;
    handleSessionSelection(session.session_id, selected);
  };
  const onDeleteModalOpen = () => {
    if (isOfflineSyntheticSession) return;
    setSelectedSessionIds([session.session_id]);
  };

  return (
    <>
      {showDateHeader && (
        <div className="p-1.5">
          <TypographyCaptionBold className="text-text-secondary">
            {sessionDateDisplay}
          </TypographyCaptionBold>
        </div>
      )}
      <div
        data-testid="session-list-session-item"
        className={cn(
          'w-full',
          index !== sessionSearchResult.length - 1 && 'mb-1',
          shouldHighlightFirstSession && index === 0 && 'animation-ping'
        )}
        id={
          session.session_id === sessions.at(-1)?.session_id && !isFetchingNextPage
            ? session.session_id
            : undefined
        }
      >
        <AccordionNavV3Button
          shouldGoTutorialNextStep={shouldHighlightFirstSession}
          session={session}
          integrationSwitchPatient={integrationSwitchPatient}
          time={timeFormatter.format(sessionTime)}
          onMobileSideBarDismiss={onMobileSideBarDismiss}
          showCheckbox={showCheckbox && !isOfflineSyntheticSession}
          selected={!isOfflineSyntheticSession && isSelected}
          onSelected={onSelected}
          onDeleteModalOpen={onDeleteModalOpen}
          onSessionsDeleted={onSessionDeleted}
        />
      </div>
      {isFetchingNextPage && index === sessionSearchResult.length - 1 && (
        <div className="mt-2 flex items-center justify-center text-text-secondary">
          <LoaderCircle size={20} className="animate-spin" />
        </div>
      )}
    </>
  );
});
